import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Folder,
  FolderOpen,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  ChevronRight,
  ChevronDown,
  FolderPlus,
  RefreshCw,
  Tag,
} from "lucide-react";

const COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#f43f5e",
  "#f97316", "#eab308", "#22c55e", "#14b8a6",
  "#06b6d4", "#3b82f6", "#64748b",
];

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [migrating, setMigrating] = useState(false);
  const [productCounts, setProductCounts] = useState({});

  // Dialog state
  const [dialog, setDialog] = useState(null); // {mode: "create"|"edit", cat?: obj, parentId?: str}
  const [formName, setFormName] = useState("");
  const [formColor, setFormColor] = useState(COLORS[0]);
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchProductCounts();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await api.get("/categories");
      setCategories(data);
      // Expand all parents by default
      const parentIds = data.filter(c => !c.parent_id).map(c => c.id);
      const exp = {};
      parentIds.forEach(id => { exp[id] = true; });
      setExpanded(exp);
    } catch {
      toast.error("Kategoriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchProductCounts = async () => {
    try {
      const products = await api.get("/products?limit=5000");
      const counts = {};
      products.forEach(p => {
        if (p.category_id) counts[p.category_id] = (counts[p.category_id] || 0) + 1;
        if (p.subcategory_id) counts[p.subcategory_id] = (counts[p.subcategory_id] || 0) + 1;
      });
      setProductCounts(counts);
    } catch {}
  };

  const parents = categories.filter(c => !c.parent_id);
  const children = (parentId) => categories.filter(c => c.parent_id === parentId);

  const openCreate = (parentId = null) => {
    setFormName("");
    setFormColor(COLORS[0]);
    setDialog({ mode: "create", parentId });
  };

  const openEdit = (cat) => {
    setFormName(cat.name);
    setFormColor(cat.color || COLORS[0]);
    setDialog({ mode: "edit", cat });
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error("Kategori adı zorunludur"); return; }
    setSaving(true);
    try {
      if (dialog.mode === "create") {
        await api.post("/categories", {
          name: formName.trim(),
          parent_id: dialog.parentId || null,
          color: formColor,
          order: 0,
        });
        toast.success("Kategori oluşturuldu");
      } else {
        await api.patch(`/categories/${dialog.cat.id}`, {
          name: formName.trim(),
          color: formColor,
        });
        toast.success("Kategori güncellendi");
      }
      setDialog(null);
      await fetchCategories();
      await fetchProductCounts();
    } catch (e) {
      toast.error(e.message || "Hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/categories/${deleteTarget.id}`);
      toast.success(`"${deleteTarget.name}" silindi`);
      setDeleteTarget(null);
      await fetchCategories();
      await fetchProductCounts();
    } catch (e) {
      toast.error(e.message || "Silinemedi");
    }
  };

  const handleMigrate = async () => {
    setMigrating(true);
    try {
      const res = await api.post("/categories/migrate-from-strings");
      toast.success(`${res.categories_created} kategori oluşturuldu, ${res.products_updated} ürün güncellendi`);
      await fetchCategories();
      await fetchProductCounts();
    } catch (e) {
      toast.error(e.message || "Aktarım hatası");
    } finally {
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kategoriler</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Ürünlerinizi hiyerarşik kategorilere ayırın
          </p>
        </div>
        <div className="flex gap-2">
          {categories.length === 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMigrate}
              disabled={migrating}
              className="text-violet-700 border-violet-200 hover:bg-violet-50"
            >
              {migrating ? (
                <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              )}
              Mevcut Kategorileri Aktar
            </Button>
          )}
          <Button size="sm" onClick={() => openCreate()} className="bg-violet-600 hover:bg-violet-700">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Yeni Kategori
          </Button>
        </div>
      </div>

      {/* Migrate banner */}
      {categories.length > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-violet-100 bg-violet-50 px-4 py-2.5 text-sm text-violet-700">
          <span>Eski string kategorilerini bu sisteme aktarmak ister misiniz?</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMigrate}
            disabled={migrating}
            className="text-violet-700 hover:bg-violet-100 h-7 text-xs"
          >
            {migrating ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : null}
            Aktar
          </Button>
        </div>
      )}

      {/* Empty state */}
      {parents.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-200 p-12 text-center">
          <Tag className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Henüz kategori yok</p>
          <p className="text-sm text-gray-400 mt-1 mb-4">
            Ürünlerinizi düzenlemek için kategori oluşturun ya da mevcut eski kategorileri aktarın.
          </p>
          <div className="flex gap-2 justify-center">
            <Button size="sm" variant="outline" onClick={handleMigrate} disabled={migrating}>
              {migrating ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Eski Kategorileri Aktar
            </Button>
            <Button size="sm" onClick={() => openCreate()} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Kategori Oluştur
            </Button>
          </div>
        </div>
      )}

      {/* Category tree */}
      <div className="space-y-2">
        {parents.map((parent) => {
          const subs = children(parent.id);
          const isExpanded = expanded[parent.id] ?? true;
          const totalProducts = (productCounts[parent.id] || 0) +
            subs.reduce((s, c) => s + (productCounts[c.id] || 0), 0);
          return (
            <div key={parent.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              {/* Parent row */}
              <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                <button
                  type="button"
                  onClick={() => setExpanded(e => ({ ...e, [parent.id]: !isExpanded }))}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {subs.length > 0 ? (
                    isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                  ) : (
                    <span className="h-4 w-4 block" />
                  )}
                </button>
                <div
                  className="h-3 w-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: parent.color || "#6366f1" }}
                />
                <span className="font-medium text-gray-800 flex-1">{parent.name}</span>
                <div className="flex items-center gap-2">
                  {subs.length > 0 && (
                    <span className="text-xs text-gray-400">{subs.length} alt kategori</span>
                  )}
                  {totalProducts > 0 && (
                    <Badge variant="secondary" className="text-xs h-5">
                      {totalProducts} ürün
                    </Badge>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-700">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openCreate(parent.id)}>
                      <FolderPlus className="h-4 w-4 mr-2" />
                      Alt Kategori Ekle
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openEdit(parent)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Düzenle
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setDeleteTarget(parent)}
                      className="text-red-600 focus:text-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Sil
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Children */}
              {isExpanded && subs.length > 0 && (
                <div className="border-t border-gray-100">
                  {subs.map((sub) => (
                    <div key={sub.id} className="flex items-center gap-3 px-4 py-2.5 pl-12 hover:bg-gray-50 border-b last:border-b-0 border-gray-50">
                      <div
                        className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: sub.color || parent.color || "#6366f1" }}
                      />
                      <span className="text-sm text-gray-700 flex-1">{sub.name}</span>
                      {productCounts[sub.id] > 0 && (
                        <Badge variant="secondary" className="text-xs h-5">
                          {productCounts[sub.id]} ürün
                        </Badge>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-gray-400 hover:text-gray-700">
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(sub)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Düzenle
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteTarget(sub)}
                            className="text-red-600 focus:text-red-700"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Sil
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                  {/* Add sub button */}
                  <button
                    type="button"
                    onClick={() => openCreate(parent.id)}
                    className="flex items-center gap-2 px-4 py-2 pl-12 w-full text-xs text-violet-500 hover:text-violet-700 hover:bg-violet-50/50 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Alt kategori ekle
                  </button>
                </div>
              )}
              {isExpanded && subs.length === 0 && (
                <button
                  type="button"
                  onClick={() => openCreate(parent.id)}
                  className="flex items-center gap-2 px-4 py-2 pl-12 w-full text-xs text-gray-400 hover:text-violet-600 hover:bg-violet-50/50 border-t border-gray-50 transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Alt kategori ekle
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === "create"
                ? dialog?.parentId ? "Alt Kategori Ekle" : "Yeni Kategori"
                : "Kategoriyi Düzenle"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Kategori Adı</label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Örn: Bisiklet Aksesuarları"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Renk</label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setFormColor(c)}
                    className="h-7 w-7 rounded-full border-2 transition-all"
                    style={{
                      backgroundColor: c,
                      borderColor: formColor === c ? "white" : c,
                      boxShadow: formColor === c ? `0 0 0 2px ${c}` : "none",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>İptal</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kategoriyi sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>"{deleteTarget?.name}"</strong> kategorisi silinecek.
              {children(deleteTarget?.id || "").length > 0 && (
                <span className="block mt-1 text-red-600">
                  Bu kategorinin alt kategorileri de silinecek!
                </span>
              )}
              <span className="block mt-1">
                Bu kategorideki ürünler kategorisiz kalacak.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
